'use client';

import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { randomAddressGeneratorContent } from '../../data/random-address-generator-content';
import Link from 'next/link';

// Types
type Address = {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
};

// Sample data
const streets = ['Main St', 'Oak Ave', 'Maple Dr', 'Cedar Ln', 'Pine St', 'Elm St'];
const cities = ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix', 'Philadelphia'];
const states = [
    { name: 'Alabama', abbr: 'AL' },
    { name: 'Alaska', abbr: 'AK' },
    { name: 'Arizona', abbr: 'AZ' },
    { name: 'Arkansas', abbr: 'AR' },
    { name: 'California', abbr: 'CA' },
    { name: 'Colorado', abbr: 'CO' },
    { name: 'Connecticut', abbr: 'CT' },
    { name: 'Delaware', abbr: 'DE' },
    { name: 'Florida', abbr: 'FL' },
    { name: 'Georgia', abbr: 'GA' },
];

const countries = ['United States', 'Canada', 'United Kingdom', 'Australia'];

export default function RandomAddressGenerator() {
    const [address, setAddress] = useState<Address>(generateRandomAddress());
    const [copied, setCopied] = useState(false);
    const { toast } = useToast();

    function generateRandomAddress(): Address {
        const streetNumber = Math.floor(Math.random() * 9999) + 1;
        const street = `${streetNumber} ${streets[Math.floor(Math.random() * streets.length)]}`;
        const city = cities[Math.floor(Math.random() * cities.length)];
        const state = states[Math.floor(Math.random() * states.length)];
        const zipCode = Math.floor(10000 + Math.random() * 90000).toString();
        const country = countries[Math.floor(Math.random() * countries.length)];

        return {
            street,
            city,
            state: state.name,
            zipCode,
            country,
        };
    }

    const generateNewAddress = useCallback(() => {
        setAddress(generateRandomAddress());
    }, []);

    const copyToClipboard = () => {
        const addressText = `${address.street}\n${address.city}, ${address.state} ${address.zipCode}\n${address.country}`;

        navigator.clipboard.writeText(addressText).then(() => {
            setCopied(true);
            toast({
                title: 'Copied!',
                description: 'Address copied to clipboard',
                variant: 'success'
            });

            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="max-w-3xl mx-auto">
                <ToolPageIntro title="Random Address Generator">
                Generate random addresses with our free online tool. Create realistic addresses for testing, data generation, and privacy protection.
            </ToolPageIntro>

                <Card className="mb-8">
                    <CardHeader>
                        <div className="flex justify-between items-center">
                            <CardTitle>Generated Address</CardTitle>
                            <div className="flex space-x-2">
                                <Button variant="outline" size="sm" onClick={copyToClipboard} disabled={copied}>
                                    <Copy className="h-4 w-4 mr-2" />
                                    {copied ? 'Copied!' : 'Copy'}
                                </Button>
                                <Button variant="outline" size="sm" onClick={generateNewAddress}>
                                    <RefreshCw className="h-4 w-4 mr-2" />
                                    Generate New
                                </Button>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="bg-muted/50 p-6 rounded-lg font-mono">
                            <p className="whitespace-pre-line">
                                {`${address.street}
${address.city}, ${address.state} ${address.zipCode}
${address.country}`}
                            </p>
                        </div>
                    </CardContent>
                </Card>

                <div className="grid gap-6 md:grid-cols-2">
                    <Card>
                        <CardHeader>
                            <CardTitle>Address Details</CardTitle>
                            <CardDescription>Individual components of the address</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div>
                                <p className="text-sm text-muted-foreground">Street</p>
                                <p>{address.street}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">City</p>
                                <p>{address.city}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">State</p>
                                <p>{address.state}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">ZIP Code</p>
                                <p>{address.zipCode}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Country</p>
                                <p>{address.country}</p>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Formats</CardTitle>
                            <CardDescription>Different address formats</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div>
                                <p className="text-sm text-muted-foreground mb-1">Single Line</p>
                                <p className="bg-muted/50 p-3 rounded">
                                    {`${address.street}, ${address.city}, ${address.state} ${address.zipCode}, ${address.country}`}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground mb-1">Mailing Format</p>
                                <div className="bg-muted/50 p-3 rounded font-mono">
                                    <pre className="whitespace-pre font-sans">
                                        {`${address.street}
${address.city}, ${address.state} ${address.zipCode}
${address.country}`}
                                    </pre>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={randomAddressGeneratorContent.title}
                description={randomAddressGeneratorContent.description}
                content={randomAddressGeneratorContent.content}
                faqs={randomAddressGeneratorContent.faqs}
                relatedTools={randomAddressGeneratorContent.relatedTools}
            />
        </div>
    );
}
